import { useEffect, useRef, useState } from 'react';
import { Download, FileText, LoaderCircle } from 'lucide-react';
import { ExportState, ReportArea, ReportFilters } from 'modules/arena/reports/model/types';
import { ReportsSource } from 'modules/arena/reports/source/types';
import { Button } from 'shared/components/ui/Button';

interface ExportControlProps {
  source: ReportsSource;
  area: ReportArea;
  filters: ReportFilters;
  canExport: boolean;
  /** Motivo temporário para não exportar (carregando, sem acesso, erro). */
  blockedReason?: string;
}

/**
 * Exporta o período FILTRADO E APLICADO, com todas as linhas (a fonte gera o arquivo, não a
 * página visível). PDF só aparece habilitado quando a integração oferece.
 */
export function ExportControl({ source, area, filters, canExport, blockedReason }: ExportControlProps) {
  const [state, setState] = useState<ExportState>({ status: 'idle' });
  const controllerRef = useRef<AbortController | null>(null);
  const { csv, pdf } = source.capabilities.export;

  // Filtros ou área mudaram: o arquivo anterior não representa mais o que está na tela.
  useEffect(() => {
    controllerRef.current?.abort();
    setState({ status: 'idle' });
  }, [area, filters, source]);

  useEffect(() => {
    if (state.status !== 'ready' || !state.expiresAt) return;
    const timer = window.setTimeout(() => setState({ status: 'expired' }), state.expiresAt - Date.now());
    return () => window.clearTimeout(timer);
  }, [state]);

  if (!csv || !canExport || blockedReason) {
    return (
      <p className="text-xs text-ink-secondary">
        {!canExport ? 'Seu perfil não pode exportar relatórios.' : blockedReason ?? 'Exportação indisponível nesta integração.'}
      </p>
    );
  }

  async function generate() {
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ status: 'generating' });
    try {
      setState(await source.exportArea(area, filters, controller.signal));
    } catch (error) {
      if ((error as Error).name !== 'AbortError') setState({ status: 'error', message: (error as Error).message });
    }
  }

  function download() {
    if (state.status !== 'ready') return;
    const url = URL.createObjectURL(new Blob([state.content], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = state.fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-wrap items-center gap-2" aria-live="polite">
      {state.status === 'ready' ? (
        <>
          <Button className="px-3 py-1.5 text-sm" onClick={download}>
            <Download className="h-4 w-4" aria-hidden="true" />
            Baixar CSV
          </Button>
          <span className="text-xs text-ink-secondary">
            {state.scope}
            {state.expiresAt &&
              ` · disponível até ${new Date(state.expiresAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
          </span>
        </>
      ) : (
        <Button variant="neutral" className="px-3 py-1.5 text-sm" onClick={generate} disabled={state.status === 'generating'}>
          {state.status === 'generating' ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {state.status === 'generating' ? 'Gerando arquivo…' : 'Exportar período filtrado (CSV)'}
        </Button>
      )}
      <Button variant="neutral" className="px-3 py-1.5 text-sm" disabled title="A integração ainda não gera PDF">
        <FileText className="h-4 w-4" aria-hidden="true" />
        PDF indisponível
      </Button>
      {state.status === 'error' && <span className="text-xs font-medium text-danger">{state.message}</span>}
      {state.status === 'expired' && <span className="text-xs text-ink-secondary">O arquivo expirou. Gere novamente.</span>}
      {!pdf && <span className="sr-only">Exportação em PDF não suportada pela integração.</span>}
    </div>
  );
}
